import { create } from 'zustand'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DownloadStatus = 'idle' | 'queued' | 'downloading' | 'saving' | 'done' | 'error'

export type EpisodeDownloadInfo = {
  episodeId: number
  productId: number
  domain: string
  title: string
  /** 0–100 */
  progress: number
  status: DownloadStatus
  error?: string
}

type DownloadState = {
  /** エピソードIDごとのダウンロード情報 */
  downloads: Map<number, EpisodeDownloadInfo>
  /** 現在ダウンロード中のエピソードID（並列制御用） */
  activeDownloadId: number | null
  /** ダウンロードキュー（FIFO） */
  queue: number[]
}

type DownloadActions = {
  /** キューにエピソード追加 */
  enqueue: (info: Omit<EpisodeDownloadInfo, 'progress' | 'status'>) => void
  /** ダウンロード開始（activeに設定） */
  startDownload: (episodeId: number) => void
  /** 進捗更新 */
  updateProgress: (episodeId: number, progress: number) => void
  /** ステータス変更 */
  setStatus: (episodeId: number, status: DownloadStatus, error?: string) => void
  /** ダウンロード完了 → 次のキューアイテムを返す */
  completeDownload: (episodeId: number) => number | undefined
  /** ダウンロードキャンセル */
  cancelDownload: (episodeId: number) => void
  /** 全キュークリア */
  clearQueue: () => void
  /** 特定エピソードの情報取得 */
  getDownload: (episodeId: number) => EpisodeDownloadInfo | undefined
}

export const useDownloadStore = create<DownloadState & DownloadActions>((set, get) => ({
  downloads: new Map(),
  activeDownloadId: null,
  queue: [],

  enqueue: (info) => {
    set((state) => {
      const downloads = new Map(state.downloads)
      // 既に存在する場合はスキップ
      if (downloads.has(info.episodeId)) return state

      downloads.set(info.episodeId, {
        ...info,
        progress: 0,
        status: 'queued',
      })
      return {
        downloads,
        queue: [...state.queue, info.episodeId],
      }
    })
  },

  startDownload: (episodeId) => {
    set((state) => {
      const downloads = new Map(state.downloads)
      const existing = downloads.get(episodeId)
      if (!existing) return state

      downloads.set(episodeId, { ...existing, status: 'downloading', progress: 0 })
      return {
        downloads,
        activeDownloadId: episodeId,
        queue: state.queue.filter((id) => id !== episodeId),
      }
    })
  },

  updateProgress: (episodeId, progress) => {
    set((state) => {
      const downloads = new Map(state.downloads)
      const existing = downloads.get(episodeId)
      if (!existing) return state

      downloads.set(episodeId, { ...existing, progress })
      return { downloads }
    })
  },

  setStatus: (episodeId, status, error) => {
    set((state) => {
      const downloads = new Map(state.downloads)
      const existing = downloads.get(episodeId)
      if (!existing) return state

      downloads.set(episodeId, { ...existing, status, error })
      return { downloads }
    })
  },

  completeDownload: (episodeId) => {
    // NOTE: set() 内からは値を返せないため、クロージャ変数で次のキューIDを外に渡す
    let nextId: number | undefined
    set((state) => {
      const downloads = new Map(state.downloads)
      const existing = downloads.get(episodeId)
      if (existing) {
        downloads.set(episodeId, { ...existing, status: 'done', progress: 100 })
      }
      nextId = state.queue.at(0)
      return {
        downloads,
        activeDownloadId: null,
      }
    })
    return nextId
  },

  cancelDownload: (episodeId) => {
    set((state) => {
      const downloads = new Map(state.downloads)
      downloads.delete(episodeId)
      return {
        downloads,
        queue: state.queue.filter((id) => id !== episodeId),
        activeDownloadId: state.activeDownloadId === episodeId ? null : state.activeDownloadId,
      }
    })
  },

  clearQueue: () => {
    set((state) => {
      const downloads = new Map(state.downloads)
      // キュー内のアイテムのみ削除（activeやdoneは残す）
      for (const id of state.queue) {
        downloads.delete(id)
      }
      return { downloads, queue: [] }
    })
  },

  getDownload: (episodeId) => {
    return get().downloads.get(episodeId)
  },
}))
